import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Users,
  MessageCircle,
  Search,
  Plus,
  Heart,
  Briefcase,
  Brain,
  Home,
  Activity,
  Coffee,
  UserPlus,
  LogOut,
  Send
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOPIC_ICONS = {
  ptsd: Brain,
  transition: Briefcase,
  family: Home,
  career: Briefcase,
  health: Activity,
  mental_health: Heart,
  substance: Coffee,
  general: MessageCircle
};

const SupportGroups = ({ user }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  
  // Create group form
  const [newGroup, setNewGroup] = useState({
    name: "",
    topic: "general",
    description: "",
    is_private: false
  });

  useEffect(() => {
    loadData();
  }, [selectedTopic]);

  useEffect(() => {
    let interval;
    if (selectedGroup) {
      interval = setInterval(() => loadGroupMessages(selectedGroup.group_id), 5000);
    }
    return () => clearInterval(interval);
  }, [selectedGroup]);

  const loadData = async () => {
    try {
      const [topicsRes, groupsRes, myGroupsRes] = await Promise.all([
        axios.get(`${API}/groups/topics`),
        axios.get(`${API}/groups${selectedTopic !== "all" ? `?topic=${selectedTopic}` : ""}`),
        axios.get(`${API}/groups/my`)
      ]);
      
      setTopics(topicsRes.data.topics);
      setGroups(groupsRes.data.groups);
      setMyGroups(myGroupsRes.data.groups);
    } catch (error) {
      console.error("Failed to load groups:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGroupMessages = async (groupId) => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/messages`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim() || !newGroup.description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/groups`, newGroup);
      toast.success("Support group created!");
      setShowCreateDialog(false);
      setNewGroup({ name: "", topic: "general", description: "", is_private: false });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create group");
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/join`);
      toast.success("Joined group successfully!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join group");
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/leave`);
      toast.success("Left group");
      if (selectedGroup?.group_id === groupId) {
        setSelectedGroup(null);
        setMessages([]);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave group");
    }
  };

  const handleOpenGroup = async (group) => {
    setSelectedGroup(group);
    await loadGroupMessages(group.group_id);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedGroup) return;

    setIsSending(true);
    try {
      await axios.post(`${API}/groups/${selectedGroup.group_id}/messages`, {
        content: newMessage
      });
      setNewMessage("");
      await loadGroupMessages(selectedGroup.group_id);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const getTopicIcon = (topicId) => {
    const Icon = TOPIC_ICONS[topicId] || MessageCircle;
    return <Icon className="w-5 h-5" />;
  };

  const filteredGroups = groups.filter(g => 
    g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading support groups...</p>
        </div>
      </div>
    );
  }

  // Group Chat View
  if (selectedGroup) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => setSelectedGroup(null)}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    {getTopicIcon(selectedGroup.topic)}
                    {selectedGroup.name}
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    {selectedGroup.member_count} members
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleLeaveGroup(selectedGroup.group_id)}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Leave
              </Button>
            </div>
          </div>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 max-w-4xl mx-auto w-full">
          <div className="space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.message_id}
                  className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      msg.sender_id === user?.user_id
                        ? "bg-primary text-white"
                        : "bg-muted"
                    }`}
                  >
                    {msg.sender_id !== user?.user_id && (
                      <p className="text-xs font-medium mb-1 opacity-70">{msg.sender_name}</p>
                    )}
                    <p className="text-sm">{msg.content}</p>
                    <p className="text-xs opacity-60 mt-1">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Input */}
        <div className="border-t border-border bg-card p-4">
          <div className="max-w-4xl mx-auto flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
              data-testid="group-message-input"
            />
            <Button 
              onClick={handleSendMessage} 
              disabled={isSending || !newMessage.trim()}
              data-testid="send-group-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Support Groups
                </h1>
                <p className="text-sm text-muted-foreground">
                  Connect with peers who understand
                </p>
              </div>
            </div>
            {(user?.role === "responder" || user?.is_admin) && (
              <Button onClick={() => setShowCreateDialog(true)} data-testid="create-group-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Group
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* My Groups */}
        {myGroups.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4">My Groups</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {myGroups.map((group) => (
                <Card 
                  key={group.group_id} 
                  className="card-valor cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleOpenGroup(group)}
                  data-testid={`my-group-${group.group_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        {getTopicIcon(group.topic)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{group.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-1">{group.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            <Users className="w-3 h-3 mr-1" />
                            {group.member_count}
                          </Badge>
                          {group.is_moderator && (
                            <Badge variant="secondary" className="text-xs">Moderator</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search groups..."
              className="pl-10"
              data-testid="search-groups-input"
            />
          </div>
          <Select value={selectedTopic} onValueChange={setSelectedTopic}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="topic-filter">
              <SelectValue placeholder="All Topics" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Topics</SelectItem>
              {topics.map((topic) => (
                <SelectItem key={topic.id} value={topic.id}>{topic.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Available Groups */}
        <h2 className="text-lg font-semibold mb-4">Discover Groups</h2>
        {filteredGroups.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {filteredGroups.map((group) => (
              <Card key={group.group_id} className="card-valor" data-testid={`group-${group.group_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      {getTopicIcon(group.topic)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{group.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">{group.description}</p>
                      <div className="flex items-center justify-between mt-3">
                        <Badge variant="outline" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          {group.member_count} members
                        </Badge>
                        {group.is_member ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleOpenGroup(group)}
                          >
                            Open Chat
                          </Button>
                        ) : (
                          <Button 
                            size="sm"
                            onClick={() => handleJoinGroup(group.group_id)}
                            data-testid={`join-group-${group.group_id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            Join
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-valor">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No groups found matching your criteria</p>
              {(user?.role === "responder" || user?.is_admin) && (
                <Button 
                  className="mt-4" 
                  onClick={() => setShowCreateDialog(true)}
                >
                  Create the First Group
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Support Group</DialogTitle>
            <DialogDescription>
              Create a new group for veterans to connect and support each other
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Group Name *</label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                placeholder="e.g., PTSD Warriors"
                data-testid="new-group-name"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Topic *</label>
              <Select 
                value={newGroup.topic} 
                onValueChange={(v) => setNewGroup({...newGroup, topic: v})}
              >
                <SelectTrigger data-testid="new-group-topic">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {topics.map((topic) => (
                    <SelectItem key={topic.id} value={topic.id}>{topic.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Description *</label>
              <Textarea
                value={newGroup.description}
                onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                placeholder="Describe what this group is about..."
                rows={3}
                data-testid="new-group-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateGroup} data-testid="submit-create-group">
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportGroups;
